import { Form, Input, Typography } from "antd";

import { SLUG_FORMAT_REGEX } from "src/utils/helper";

/**
 * The URL slug field for modules, lecture sets, lectures and problem sets.
 *
 * On create, the field is optional. If it is empty, the backend makes the slug from the title.
 * On edit, the field is required. A new value causes a rename. The previous slugs continue to
 * work, so learner links do not break.
 *
 * @param {Object} props
 * @param {boolean} props.isEdit - True if the form edits an existing entity.
 * @param {string} props.entityName - The entity name for the messages, for example "lecture set".
 * @param {Array<string>} [props.reservedSlugs=[]] - All the slugs that the entity used.
 */
const SlugFormItem = ({ isEdit, entityName, reservedSlugs = [] }) => {
  const form = Form.useFormInstance();
  const currentSlug = Form.useWatch("slug", form);
  const previousSlugs = reservedSlugs.filter((slug) => slug !== currentSlug);

  const help = isEdit
    ? "A change to the slug keeps the previous URLs working. Change it only if necessary."
    : "Optional. If you leave it empty, the slug comes from the title.";

  return (
    <Form.Item
      name="slug"
      label="Slug"
      normalize={(value) => value?.toLowerCase()}
      rules={[
        {
          required: isEdit,
          message: `Please input the slug of the ${entityName}!`
        },
        {
          pattern: SLUG_FORMAT_REGEX,
          message: "Use lowercase letters, digits and single hyphens only. Example: two-pointers."
        }
      ]}
      extra={
        <>
          <Typography.Text type="secondary">{help}</Typography.Text>
          {isEdit && previousSlugs.length > 0 && (
            <Typography.Text type="secondary" style={{ display: "block" }}>
              Previous slugs: {previousSlugs.join(", ")}
            </Typography.Text>
          )}
        </>
      }
    >
      <Input placeholder={isEdit ? undefined : "Leave empty to use the title"} />
    </Form.Item>
  );
};

/**
 * Gives the previous slugs of an entity as text for the detail panels.
 *
 * @param {Object} entity - A module, lecture set or problem set node from the admin tree.
 * @returns {string} The previous slugs, separated by commas, or "None".
 */
export const formatPreviousSlugs = ({ slug, reservedSlugs = [] } = {}) => {
  const previousSlugs = reservedSlugs.filter((reservedSlug) => reservedSlug !== slug);
  return previousSlugs.length > 0 ? previousSlugs.join(", ") : "None";
};

export default SlugFormItem;
